import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowLeft, Plus, Edit, Trash2, Loader2, FolderCog, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseSettingsPage = () => {
  const [caseStatuses, setCaseStatuses] = useState([]);
  const [priorityLevels, setPriorityLevels] = useState([]);
  const [closureReasons, setClosureReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('statuses');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('status'); // status, priority, closure
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#6366f1', is_active: true, sort_order: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusesRes, prioritiesRes, closuresRes] = await Promise.all([
        axios.get(`${API}/admin/case-statuses`),
        axios.get(`${API}/admin/priority-levels`),
        axios.get(`${API}/admin/closure-reasons`)
      ]);
      setCaseStatuses(statusesRes.data.statuses || []);
      setPriorityLevels(prioritiesRes.data.priorities || []);
      setClosureReasons(closuresRes.data.reasons || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type, item = null) => {
    setDialogType(type);
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        color: item.color || '#6366f1',
        is_active: item.is_active !== false,
        sort_order: item.sort_order || 0
      });
    } else {
      setEditingItem(null);
      setFormData({ name: '', description: '', color: '#6366f1', is_active: true, sort_order: 0 });
    }
    setDialogOpen(true);
  };

  const saveItem = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setSaving(true);
    const endpoint = {
      status: 'case-statuses',
      priority: 'priority-levels',
      closure: 'closure-reasons'
    }[dialogType];
    
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/${endpoint}/${editingItem.id}`, formData);
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/admin/${endpoint}`, formData);
        toast.success('Created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (type, itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    const endpoint = {
      status: 'case-statuses',
      priority: 'priority-levels',
      closure: 'closure-reasons'
    }[type];
    
    try {
      await axios.delete(`${API}/admin/${endpoint}/${itemId}`);
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getDialogTitle = () => {
    const action = editingItem ? 'Edit' : 'Add';
    const type = {
      status: 'Case Status',
      priority: 'Priority Level',
      closure: 'Closure Reason'
    }[dialogType];
    return `${action} ${type}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="case-settings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FolderCog className="w-6 h-6" />
              Case Management Settings
            </h1>
            <p className="text-muted-foreground">Configure case statuses, priorities, and closure reasons</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <Button
          variant={activeTab === 'statuses' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('statuses')}
          className="rounded-b-none"
        >
          <CheckCircle2 className="w-4 h-4 mr-2" />
          Statuses ({caseStatuses.length})
        </Button>
        <Button
          variant={activeTab === 'priorities' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('priorities')}
          className="rounded-b-none"
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          Priorities ({priorityLevels.length})
        </Button>
        <Button
          variant={activeTab === 'closures' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('closures')}
          className="rounded-b-none"
        >
          <XCircle className="w-4 h-4 mr-2" />
          Closure Reasons ({closureReasons.length})
        </Button>
      </div>

      {/* Statuses Tab */}
      {activeTab === 'statuses' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Case Statuses</CardTitle>
              <CardDescription>Define possible case statuses</CardDescription>
            </div>
            <Button onClick={() => openDialog('status')}>
              <Plus className="w-4 h-4 mr-2" />
              Add Status
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status Name</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {caseStatuses.map((status) => (
                  <TableRow key={status.id}>
                    <TableCell className="font-medium">{status.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: status.color }} />
                        <span className="text-sm text-muted-foreground">{status.color}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={status.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                        {status.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDialog('status', status)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem('status', status.id)} className="text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {caseStatuses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No case statuses defined
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Priorities Tab */}
      {activeTab === 'priorities' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Priority Levels</CardTitle>
              <CardDescription>Define case priority levels</CardDescription>
            </div>
            <Button onClick={() => openDialog('priority')}>
              <Plus className="w-4 h-4 mr-2" />
              Add Priority
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Priority Name</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Sort Order</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {priorityLevels.map((priority) => (
                  <TableRow key={priority.id}>
                    <TableCell className="font-medium">{priority.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: priority.color }} />
                        <span className="text-sm text-muted-foreground">{priority.color}</span>
                      </div>
                    </TableCell>
                    <TableCell>{priority.sort_order}</TableCell>
                    <TableCell>
                      <Badge className={priority.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                        {priority.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDialog('priority', priority)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem('priority', priority.id)} className="text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {priorityLevels.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No priority levels defined
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Closure Reasons Tab */}
      {activeTab === 'closures' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Closure Reasons</CardTitle>
              <CardDescription>Define reasons for closing cases</CardDescription>
            </div>
            <Button onClick={() => openDialog('closure')}>
              <Plus className="w-4 h-4 mr-2" />
              Add Reason
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reason</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {closureReasons.map((reason) => (
                  <TableRow key={reason.id}>
                    <TableCell className="font-medium">{reason.name}</TableCell>
                    <TableCell className="text-muted-foreground">{reason.description || '-'}</TableCell>
                    <TableCell>
                      <Badge className={reason.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                        {reason.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDialog('closure', reason)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem('closure', reason.id)} className="text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {closureReasons.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No closure reasons defined
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription>Configure the details below</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            {dialogType !== 'closure' && (
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            )}
            {dialogType === 'priority' && (
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Active</p>
                <p className="text-sm text-muted-foreground">Available for selection</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveItem} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseSettingsPage;
